"use client";

import { cn } from "@/lib/utils";
import React, { useState } from "react";
import { ButtonStyleType, SpacingType } from "@/types";
import { sectionVariants } from "@/types";
import { Button } from "@/components/ui/button";
import Image from '@/components/ui/Image';
import Link from "next/link";
import { VideoIframe, useVideo } from "@/components/components/VideoIframe";
import parse from "html-react-parser";
import { SectionWrapper } from "../components/SectionWraper";
import { Badge } from "@/components/ui/badge";

interface Feature2Props { 
  darkMode?: boolean; 
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  mediaPosition?: "left" | "right";
  mediaPositionMobile?: "top" | "bottom";
  removePaddingTop?: boolean;
  removePaddingBottom?: boolean;
  badge?: string;
  title?: string; 
  content?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  microCopy?: string;
  secondaryButton?: boolean;
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
  secondaryButtonStyle?: ButtonStyleType;
  featuredItem?: "video" | "image";
  withBorder?: boolean;
  video?: string;
  loopVideo?: string;
  image?: string; 
  imageAltText?: string;  
  attributes?: React.HTMLProps<HTMLElement>;
}

const Feature2 = ({
  darkMode = false,
  variant = "A",
  spacing,
  usedInPageNav = false,
  pageNavTitle = "Feature 2",
  mediaPosition,
  mediaPositionMobile,
  themeChangeOnScroll = false,
  removePaddingTop = false,
  removePaddingBottom = false,
  badge,
  title,
  content,
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  microCopy,
  secondaryButton,
  secondaryButtonText,
  secondaryButtonLink,
  secondaryButtonStyle,
  featuredItem,
  withBorder = false,
  video,
  loopVideo,
  image,  
  imageAltText, 
  attributes,
}: Feature2Props) => {  
  const { isVideoOpen, currentVideo, getVideoThumbnail, handleVideoClick, handleCloseVideo } = useVideo();
  const [videoError, setVideoError] = useState(false);

  const getVideoType = (url: string) => {
    const extension = url.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'mp4':
        return 'video/mp4';
      case 'webm':
        return 'video/webm';
      case 'ogg':
        return 'video/ogg';
      case 'mov':
        return 'video/quicktime';
      case 'avi':
        return 'video/x-msvideo';
      default:
        return 'video/mp4'; // Default fallback
    }
  };

  const contentClass = cn(
    "flex w-full gap-8 lg:gap-20  overflow-hidden lg:flex-row lg:items-center ",
    mediaPosition === "left" ? "lg:flex-row" : "lg:flex-row-reverse",
    mediaPositionMobile === "top" ? "flex-col" : "flex-col-reverse",
  );

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="feature2"
      attributes={attributes}
      removePaddingTop={removePaddingTop}
      removePaddingBottom={removePaddingBottom}
    >
      <div className="container">
        <div className={contentClass} data-aos="fade-up">
          <div className="w-full shrink lg:w-1/2">
            {featuredItem === "video" && video && (
              <div 
                className="relative group cursor-pointer w-full  rounded-xl overflow-hidden" 
                onClick={() => handleVideoClick(video)}
              >
                {loopVideo && (
                  <>
                    <video 
                      preload="none" 
                      autoPlay 
                      loop 
                      muted 
                      playsInline 
                      className={cn(
                        "h-full w-full object-cover aspect-[628/400] rounded-xl", 
                        withBorder && "border border-border",
                        videoError && "hidden"
                      )}
                      onError={() => setVideoError(true)}
                    >
                      <source src={loopVideo} type={getVideoType(loopVideo)} />
                    </video>
                    <Image
                      src={image || "/placeholder.svg"}
                      alt={imageAltText || ""}
                      title={imageAltText || ""}
                      className={cn(
                        "h-full w-full object-cover aspect-[628/400] rounded-xl", 
                        withBorder && "border border-border",
                        !videoError && "hidden"
                      )}
                      width={628}
                      height={400}
                    />
                  </>
                )}
                {!loopVideo && image && (
                  <Image
                    src={image || getVideoThumbnail(video) || ''}
                    alt={imageAltText || ""}
                    title={imageAltText || ""}
                    width={1000}
                    height={1000}
                    className={cn("aspect-[628/400] w-full rounded-xl object-cover", withBorder && "border border-border")}
                  />
                )}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="flex items-center justify-center w-12 h-12">
                    <Button 
                      variant="secondary" 
                      size="iconLg"   
                      className="absolute bottom-6 left-6 bg-muted rounded-full"
                    >
                      <span className="material-symbols-outlined font-variation-filled">play_arrow</span>
                    </Button>
                  </div>
                </div>
                <VideoIframe 
                  videoUrl={currentVideo} 
                  isOpen={isVideoOpen} 
                  onClose={handleCloseVideo} 
                />
              </div>
            )}
            {featuredItem === "image" && image && (
              <Image
                src={image ?? ""}
                alt={imageAltText ?? ""}
                title={imageAltText ?? ""}
                width={1000}
                height={1000}
                className={cn("aspect-[628/400] w-full rounded-xl object-cover", withBorder && "border border-border")}
              />
            )}
          </div>
          <div className="w-full shrink lg:w-1/2 text-center sm:text-left">
            {badge && (
              <Badge variant="outline" bold className="mb-4">
                {badge}
              </Badge>
            )}
            {title && (
              <h2 className="text-center sm:text-left">
                {parse(title)}
              </h2>
            )}
            {content && (
              <div className="text-muted-foreground paragraph-lg mt-4 lg:mt-8 text-center sm:text-left">
                {parse(content)}
              </div>
            )}
            {hasButton && (
              <>
                <div className="flex flex-col sm:flex-row gap-4 mt-8">
                  <Button 
                    variant={buttonStyle}
                    size="lg"
                    asChild
                    className="w-full sm:w-auto"
                  >
                    <Link href={buttonLink ?? ""}>
                      {buttonText}
                    </Link>
                  </Button>
                  {secondaryButton && (
                    <Button 
                      variant={secondaryButtonStyle}
                      size="lg"
                      asChild
                      className="w-full sm:w-auto"
                    >
                      <Link href={secondaryButtonLink ?? ""}>
                        {secondaryButtonText}
                      </Link>
                    </Button>
                  )}  
                </div>
                {microCopy && (
                  <div className="text-center md:text-left text-muted-foreground paragraph-sm mt-4">
                    {parse(microCopy)}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

export { Feature2 };



